import { yupResolver } from "@hookform/resolvers/yup";
import { DEFAULT_SECTION_MARGINS, SectionMargins, PeopleSectionContent } from "api/shop";
import {
  HeadingTagSelect,
  PaziaFormButton,
  PaziaInput,
  PaziaSelect,
  PaziaTextarea,
  PaziaFileDropzone,
} from "components/atoms";
import { useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided, DraggableProvided, DraggableStateSnapshot } from "react-beautiful-dnd";
import { MdAdd, MdDelete, MdDragIndicator } from "react-icons/md";
import * as yup from "yup";
import { ANIMATION_OPTIONS } from "./animationOptions";
import { ButtonVariantSelect } from "./ButtonVariantSelect";
import { MARGINS_FORM_NAME, marginsSchema, SectionMarginsConfig } from "./SectionMarginsConfig";
import { useShopImageUpload } from "./useShopImageUpload";

const FORM_NAMES = {
  ANIMATION: "animation",
  HEADER_TEXT: "headerText",
  HEADER_TAG: "headerTag",
  PEOPLE: "people",
  BUTTONS: "buttons",
} as const;

const personSchema = yup.object({
  avatar: yup.mixed<string | File>().nullable(),
  name: yup.string().required("Imię jest wymagane"),
  role: yup.string().default(""),
  description: yup.string().default(""),
});

const buttonSchema = yup.object({
  label: yup.string().required("Tekst przycisku jest wymagany"),
  variant: yup.string().required("Wariant jest wymagany").default("primary"),
  href: yup.string().required("Link jest wymagany"),
});

const schema = yup
  .object({
    [FORM_NAMES.ANIMATION]: yup.string().required("Animacja jest wymagana"),
    [FORM_NAMES.HEADER_TEXT]: yup.string().default(""),
    [FORM_NAMES.HEADER_TAG]: yup.string(),
    [FORM_NAMES.PEOPLE]: yup.array().of(personSchema).min(1, "Dodaj przynajmniej jedną osobę"),
    [FORM_NAMES.BUTTONS]: yup.array().of(buttonSchema).default([]),
    [MARGINS_FORM_NAME]: marginsSchema,
  })
  .required();

interface FormPerson {
  avatar?: string | File | null;
  name: string;
  role: string;
  description: string;
}

interface FormValues {
  animation: string;
  headerText: string;
  headerTag: string;
  people: FormPerson[];
  buttons: Array<{ label: string; variant: string; href: string }>;
  margins: SectionMargins;
}

export interface PeopleSectionFormValues {
  animation: string;
  header: { text: string; tag?: string };
  people: Array<{
    avatar: string;
    name: string;
    role: string;
    description: string;
  }>;
  buttons: Array<{
    label: string;
    variant: "primary" | "secondary" | "outline";
    href: string;
  }>;
  margins?: SectionMargins;
}

interface PeopleSectionFormProps {
  onSubmit: (data: PeopleSectionFormValues) => void;
  onCancel: () => void;
  defaultValues?: PeopleSectionContent;
  isLoading?: boolean;
}

export const PeopleSectionForm = ({ onSubmit, onCancel, defaultValues, isLoading }: PeopleSectionFormProps) => {
  const { uploadImage } = useShopImageUpload();

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      [FORM_NAMES.ANIMATION]: defaultValues?.animation ?? "fade-bottom",
      [FORM_NAMES.HEADER_TEXT]: defaultValues?.header?.text ?? "",
      [FORM_NAMES.HEADER_TAG]: defaultValues?.header?.tag ?? "h2",
      [FORM_NAMES.PEOPLE]: defaultValues?.people?.map((p) => ({
        avatar: p.avatar ?? null,
        name: p.name,
        role: p.role ?? "",
        description: p.description ?? "",
      })) ?? [],
      [FORM_NAMES.BUTTONS]: defaultValues?.buttons ?? [],
      [MARGINS_FORM_NAME]: (defaultValues as any)?.margins ?? DEFAULT_SECTION_MARGINS,
    },
  });
  const { handleSubmit, control } = methods;

  const {
    fields: peopleFields,
    append: appendPerson,
    remove: removePerson,
    move: movePerson,
  } = useFieldArray({
    control,
    name: FORM_NAMES.PEOPLE,
  });

  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());

  const toggleCard = (index: number) => {
    setExpandedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedCards(new Set(peopleFields.map((_, i) => i)));
  };

  const collapseAll = () => {
    setExpandedCards(new Set());
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.source.index === result.destination.index) return;

    const from = result.source.index;
    const to = result.destination.index;

    // Update expandedCards indices to match new positions
    setExpandedCards((prev) => {
      const newSet = new Set<number>();
      for (const idx of prev) {
        if (idx === from) {
          newSet.add(to);
        } else if (from < to && idx > from && idx <= to) {
          newSet.add(idx - 1);
        } else if (from > to && idx >= to && idx < from) {
          newSet.add(idx + 1);
        } else {
          newSet.add(idx);
        }
      }
      return newSet;
    });

    movePerson(from, to);
  };

  const watchedPeople = methods.watch(FORM_NAMES.PEOPLE);

  const {
    fields: buttonFields,
    append: appendButton,
    remove: removeButton,
  } = useFieldArray({
    control,
    name: FORM_NAMES.BUTTONS,
  });

  const handleFormSubmit = async (data: FormValues) => {
    const formattedPeople = await Promise.all(
      data.people.map(async (person) => ({
        avatar: person.avatar ? await uploadImage(person.avatar) : "",
        name: person.name,
        role: person.role || "",
        description: person.description || "",
      })),
    );

    const formattedData: PeopleSectionFormValues = {
      animation: data.animation,
      header: { text: data.headerText, tag: data.headerTag as any },
      people: formattedPeople,
      buttons: data.buttons.map((b) => ({
        label: b.label,
        variant: b.variant as "primary" | "secondary" | "outline",
        href: b.href,
      })),
      margins: data.margins,
    };
    onSubmit(formattedData);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="grid gap-4">
        <div className="grid gap-3 md:grid-cols-2">
          <PaziaSelect
            name={FORM_NAMES.ANIMATION}
            label="Animacja"
            required
            options={ANIMATION_OPTIONS}
          />
          <PaziaInput name={FORM_NAMES.HEADER_TEXT} label="Nagłówek" />
        </div>

        <HeadingTagSelect name={FORM_NAMES.HEADER_TAG} />

        {/* People Section */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-slate-700 dark:text-slate-300">Osoby</h4>
            <div className="flex items-center gap-2">
              {peopleFields.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={expandAll}
                    className="rounded-md bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-300 dark:hover:bg-blue-800"
                  >
                    Rozwiń wszystkie
                  </button>
                  <button
                    type="button"
                    onClick={collapseAll}
                    className="rounded-md bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                  >
                    Zwiń wszystkie
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => {
                  appendPerson({ avatar: null, name: "", role: "", description: "" });
                  setExpandedCards((prev) => new Set([...prev, peopleFields.length]));
                }}
                className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
              >
                <MdAdd size={18} />
                Dodaj osobę
              </button>
            </div>
          </div>

          {peopleFields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak osób. Kliknij &quot;Dodaj osobę&quot;, aby dodać.
            </p>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="people">
                {(provided: DroppableProvided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-4">
                    {peopleFields.map((field, index) => {
                      const isExpanded = expandedCards.has(index);
                      const personName = watchedPeople?.[index]?.name;
                      const personAvatar = watchedPeople?.[index]?.avatar;
                      const avatarUrl = typeof personAvatar === "string" && personAvatar ? personAvatar : null;

                      return (
                        <Draggable key={field.id} draggableId={field.id} index={index}>
                          {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-600 dark:bg-slate-800 ${snapshot.isDragging ? "shadow-lg ring-2 ring-indigo-300 dark:ring-indigo-600" : ""}`}
                            >
                              {/* Card header — always visible */}
                              <div
                                className="flex cursor-pointer items-center gap-2 p-3"
                                onClick={() => toggleCard(index)}
                              >
                                <div
                                  {...provided.dragHandleProps}
                                  className="cursor-grab text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MdDragIndicator size={20} />
                                </div>

                                {avatarUrl && (
                                  <img
                                    src={avatarUrl}
                                    alt=""
                                    className="h-8 w-8 rounded-full object-cover"
                                  />
                                )}

                                <span className="flex-1 truncate text-sm font-medium text-slate-700 dark:text-slate-300">
                                  {personName || `Osoba #${index + 1}`}
                                </span>

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removePerson(index);
                                    setExpandedCards((prev) => {
                                      const newSet = new Set<number>();
                                      for (const idx of prev) {
                                        if (idx === index) continue;
                                        newSet.add(idx > index ? idx - 1 : idx);
                                      }
                                      return newSet;
                                    });
                                  }}
                                  className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                                  title="Usuń osobę"
                                >
                                  <MdDelete size={18} />
                                </button>

                                <svg
                                  className={`h-5 w-5 text-slate-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>

                              {/* Card body — only when expanded */}
                              {isExpanded && (
                                <div className="border-t border-slate-200 p-4 dark:border-slate-600">
                                  <div className="grid gap-3">
                                    <div className="grid gap-3 md:grid-cols-2">
                                      <PaziaInput
                                        name={`${FORM_NAMES.PEOPLE}.${index}.name`}
                                        label="Imię i nazwisko"
                                        required
                                      />
                                      <div className="[&_img]:max-h-20">
                                        <PaziaFileDropzone
                                          name={`${FORM_NAMES.PEOPLE}.${index}.avatar`}
                                          label="Avatar"
                                          type="image"
                                          single
                                          preview="image"
                                        />
                                      </div>
                                    </div>
                                    <PaziaInput
                                      name={`${FORM_NAMES.PEOPLE}.${index}.role`}
                                      label="Rola"
                                    />
                                    <PaziaTextarea
                                      name={`${FORM_NAMES.PEOPLE}.${index}.description`}
                                      label="Opis"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>

        {/* Buttons Section */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-slate-700 dark:text-slate-300">Przyciski</h4>
            <button
              type="button"
              onClick={() => appendButton({ label: "", variant: "primary", href: "" })}
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj przycisk
            </button>
          </div>

          {buttonFields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak przycisków. Kliknij &quot;Dodaj przycisk&quot;, aby dodać.
            </p>
          ) : (
            <div className="space-y-3">
              {buttonFields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-600 dark:bg-slate-800"
                >
                  <div className="grid flex-1 gap-3 md:grid-cols-3">
                    <PaziaInput
                      name={`${FORM_NAMES.BUTTONS}.${index}.label`}
                      label="Tekst"
                      required
                    />
                    <ButtonVariantSelect
                      name={`${FORM_NAMES.BUTTONS}.${index}.variant`}
                      required
                    />
                    <PaziaInput
                      name={`${FORM_NAMES.BUTTONS}.${index}.href`}
                      label="Link (href)"
                      required
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeButton(index)}
                    className="mt-6 rounded p-2 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                    title="Usuń przycisk"
                  >
                    <MdDelete size={20} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <SectionMarginsConfig />

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};
